import React, { useState, useEffect } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { connect } from 'react-redux';

/**
 * ImpersonationBanner - Displayed when a platform admin is impersonating a tenant user
 * Shows impersonation details and provides exit button
 */
const ImpersonationBanner = ({ auth }) => {
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (auth && auth.id) {
      checkImpersonationStatus();
    }
  }, [auth]);

  const checkImpersonationStatus = async () => {
    try {
      const response = await fetch('/api/admin/impersonate/status', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.impersonation && data.impersonation.active) {
          setImpersonation(data.impersonation);
        }
      }
    } catch (err) {
      // Silent fail - not impersonating
    } finally {
      setLoading(false);
    }
  };

  const handleEndImpersonation = async () => {
    try {
      const response = await fetch('/api/admin/impersonate', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Redirect to admin tenants page
        window.location.href = '/admin/tenants';
      }
    } catch (err) {
      console.error('Failed to end impersonation:', err);
    }
  };

  // Don't render if not impersonating
  if (loading || !impersonation) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
      padding: '0.5rem 1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>

      <span style={{ color: 'white', fontWeight: '500' }}>
        Viewing as{' '}
        <Badge bg="dark" style={{ marginLeft: '0.25rem', marginRight: '0.25rem' }}>
          {impersonation.role}
        </Badge>{' '}
        in{' '}
        <strong>{impersonation.tenantName}</strong>
      </span>

      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
        ({impersonation.impersonator?.email})
      </span>

      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
        {impersonation.remainingMinutes} min remaining
      </span>

      <Button
        variant="light"
        size="sm"
        onClick={handleEndImpersonation}
        style={{
          fontWeight: '600',
          borderRadius: '6px'
        }}
      >
        Exit
      </Button>
    </div>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(ImpersonationBanner);
